import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Tag, Button } from 'antd';
import {
  ApartmentOutlined,
  HeartOutlined,
  AuditOutlined,
  DollarOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  GiftOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import '../styles/pharmacy-tool.css';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { VIDEO_BATCH_TOOL } from '../data/videoBatchToolContent.js';
import { HEALTH_HAND_VIDEO_TOOL } from '../data/healthHandVideoToolContent.js';
import { QA_VIDEO_SKILL_TOOL } from '../data/qaVideoSkillContent.js';
import { STORY_VIDEO_SKILL_TOOL } from '../data/storyVideoSkillContent.js';
import { COMPARE_VIDEO_TOOL } from '../data/compareVideoToolContent.js';
import { EXPERT_VIDEO_SKILL_TOOL } from '../data/expertVideoSkillContent.js';
import { useToolAccess } from '../hooks/useToolAccess.js';

export function ToolsHubPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  // Thẻ công cụ chỉ ghi "Miễn phí" cho công cụ không cần gói (back-end constants/tool-access.constant.js); công cụ trả phí không ghi giá
  const { access } = useToolAccess();
  const isFree = (id) => !!access?.[id] && !access[id].requiredPlans?.some((p) => p.price > 0);

  const toolsList = [
    {
      id: 'quan-tri-thuc-chien',
      title: 'Xây dựng quy trình quản lý thực chiến (S.O.P) nhà thuốc',
      subtitle: 'Xây dựng hệ thống quy trình vận hành ( S.O.P) cho nhà thuốc',
      badge: 'ĐANG HOẠT ĐỘNG',
      badgeColor: 'success',
      image: '/images/tools/tool-quan-tri-sop.jpg',
      icon: <ApartmentOutlined style={{ fontSize: 24, color: '#005537' }} />,
      iconBg: '#ecfdf5',
      desc: 'Tự động chẩn đoán thích ứng theo quy mô quầy, tải trọn bộ 26 file Word (.docx) chuẩn để chỉnh sửa và theo dõi tiến độ áp dụng.',
      tags: [
        '6 Câu hỏi chẩn đoán cơ sở',
        '26 File Word chuẩn (.docx)',
        '5 Dòng chảy vận hành',
        'Theo dõi tiến độ realtime',
      ],
      actionText: 'Truy Cập Công Cụ',
      actionIcon: <ArrowRightOutlined />,
      actionType: 'primary',
      onClick: () => navigate('/tools/quan-tri-thuc-chien'),
      isReady: true,
      borderColor: '#10b981',
    },
    {
      id: 'do-luong-suc-khoe',
      title: 'Đo Lường Sức Khỏe Nhà Thuốc',
      subtitle: 'Bộ Chỉ Số Đánh Giá Toàn Diện & Điểm Nghẽn Vận Hành',
      badge: 'ĐANG HOẠT ĐỘNG',
      badgeColor: 'success',
      image: '/images/tools/tool-do-luong-suc-khoe.jpg',
      icon: <HeartOutlined style={{ fontSize: 24, color: '#005537' }} />,
      iconBg: '#f0fdf4',
      desc: 'Nhập 7 số liệu cơ bản, biết ngay quầy đang lãi thật hay quay vòng tiền, đối chiếu 6 chỉ số benchmark ngành và nhận bộ cẩm nang hành động riêng theo chân dung quầy.',
      tags: [
        'Không cần đăng nhập để xem sơ bộ',
        '6 Chỉ số benchmark ngành dược',
        'Bảng lãi lỗ P&L 1 tháng chi tiết',
        '4 Đòn bẩy tài chính & Bộ tư vấn riêng',
      ],
      actionText: 'Khám Sức Khỏe Quầy Ngay',
      actionIcon: <ArrowRightOutlined />,
      actionType: 'primary',
      onClick: () => navigate('/tools/do-luong-suc-khoe'),
      isReady: true,
      borderColor: '#005537',
    },
    {
      id: 'trac-nghiem-thuc-chien',
      title: 'Trắc Nghiệm Kiến Thức, Kỹ Năng',
      subtitle: 'Kiểm Tra Năng Lực Dược Sĩ & Kỹ Năng Đứng Quầy Thực Chiến',
      badge: 'ĐANG HOẠT ĐỘNG',
      badgeColor: 'success',
      image: '/images/tools/tool-trac-nghiem-ky-nang.jpg',
      icon: <AuditOutlined style={{ fontSize: 24, color: '#7c3aed' }} />,
      iconBg: '#f5f3ff',
      desc: 'Hệ thống 240 câu hỏi tình huống thực tế chia làm 4 chủ đề: Bán hàng & Chăm sóc khách, Vận hành nhà thuốc, Marketing điểm bán và Chuyên môn Dược. Vừa test vừa học qua giải thích 3 lớp.',
      tags: [
        'Kho 240 câu hỏi 4 chủ đề',
        'Bốc đề 15 câu chuẩn cấu trúc',
        'Giải thích 3 lớp & bài học quầy thuốc',
        'Xếp loại năng lực & lộ trình riêng',
      ],
      actionText: 'Làm Bài Đánh Giá Ngay',
      actionIcon: <ArrowRightOutlined />,
      actionType: 'primary',
      onClick: () => navigate('/tools/trac-nghiem-thuc-chien'),
      isReady: true,
      borderColor: '#7c3aed',
    },
    {
      id: 'co-che-luong-thuong',
      title: 'Cơ Chế Lương Thưởng Quầy Thuốc',
      subtitle: 'Xây Dựng Thang Bảng Lương 2P/3P & Quỹ Thưởng Lãi Gộp',
      badge: 'ĐANG HOẠT ĐỘNG',
      badgeColor: 'success',
      image: '/images/tools/tool-co-che-luong-thuong.jpg',
      icon: <DollarOutlined style={{ fontSize: 24, color: '#0d9488' }} />,
      iconBg: '#f0fdfa',
      desc: 'Thiết lập lương vị trí, phụ cấp năng lực CCHN & bằng cấp, quỹ thưởng trích theo lãi gộp 3 nhóm hàng. Tự động kiểm tra an toàn quỹ lương, giả lập bảng lương và xuất file Excel 7 sheet có công thức sống.',
      tags: [
        'Nghị định 293/2025/NĐ-CP (01/2026)',
        'Mô hình 2P / 3P + Thẻ điểm KPI',
        'Cân đối quỹ lương trên DT & Lãi gộp',
        'Xuất Excel 7 sheet công thức sống',
      ],
      actionText: 'Xây Cơ Chế Lương Thưởng',
      actionIcon: <ArrowRightOutlined />,
      actionType: 'primary',
      onClick: () => navigate('/tools/co-che-luong-thuong'),
      isReady: true,
      borderColor: '#0d9488',
    },
    {
      id: 'chuong-trinh-ban-hang',
      title: 'Cẩm Nang Xây Chương Trình Bán Hàng x2 Doanh Số',
      subtitle: 'Phễu Sự Kiện 2 Tầng, 11 Giải Pháp Giảm Chi Phí & Excel 10 Sheet',
      badge: 'BẢN 2.2 MỚI',
      badgeColor: 'success',
      image: '/images/tools/tool-chuong-trinh-ban-hang.jpg',
      icon: <GiftOutlined style={{ fontSize: 24, color: '#005537' }} />,
      iconBg: '#ecfdf5',
      desc: 'Xây dựng chương trình bán hàng 2 tầng theo số liệu quầy. Thiết lập phễu đo sức khỏe kéo khách & upsale đơn TPCN liệu trình, 11 giải pháp giảm chi phí kèm mẫu thư ngỏ, khóa thuốc kê đơn theo Nghị định 239/2026/NĐ-CP và xuất Excel 10 sheet sống.',
      tags: [
        '2 Tầng chương trình thực chiến',
        'Phễu đo & upsale đơn TPCN',
        '11 Giải pháp giảm chi phí',
        'Xuất Excel 10 sheet công thức sống',
      ],
      actionText: 'Lên Chương Trình Ngay',
      actionIcon: <ArrowRightOutlined />,
      actionType: 'primary',
      onClick: () => navigate('/tools/chuong-trinh-ban-hang'),
      isReady: true,
      borderColor: '#005537',
    },
    {
      id: 'gia-nhap-that',
      title: 'Giá Nhập Thật — Bóc Tách Khuyến Mại NCC',
      subtitle: 'Chia Giá & Chiết Khấu Chuẩn 5 Tầng Ngưỡng Kế Toán',
      badge: 'BETA TEST',
      badgeColor: 'warning',
      image: '/images/tools/tool-gia-nhap-that.jpg',
      icon: <DollarOutlined style={{ fontSize: 24, color: '#005537' }} />,
      iconBg: '#ecfdf5',
      desc: 'Nạp tờ CTKM, ảnh Zalo hoặc file Excel của hãng. Tự động bóc tách 5 tầng ngưỡng chiết khấu, áp bộ 20 quy tắc kế toán lọc quà ảo, tính giá nhập thực tế từng mã và xuất Excel 17 cột.',
      tags: [
        'Bóc tách 5 tầng ngưỡng chiết khấu',
        'Bộ 20 quy tắc kế toán Thế Thảo',
        'Lọc quà ảo & Cảnh báo rủi ro',
        'Xuất Excel 17 cột & So sánh 3 hãng',
      ],
      actionText: 'Bóc Tách Khuyến Mại Ngay',
      actionIcon: <ArrowRightOutlined />,
      actionType: 'primary',
      onClick: () => navigate('/tools/gia-nhap-that'),
      isReady: true,
      borderColor: '#005537',
      adminOnly: true,
    },
    {
      id: VIDEO_BATCH_TOOL.slug,
      title: VIDEO_BATCH_TOOL.name,
      subtitle: 'Phần mềm cài trên máy tính Windows',
      badge: VIDEO_BATCH_TOOL.published ? 'ĐANG HOẠT ĐỘNG' : 'BẢN NHÁP',
      badgeColor: VIDEO_BATCH_TOOL.published ? 'success' : 'warning',
      image: VIDEO_BATCH_TOOL.image || '/images/tools/tool-tao-video-hang-loat.jpg',
      icon: <VideoCameraOutlined style={{ fontSize: 24, color: '#005537' }} />,
      iconBg: '#ecfdf5',
      desc: VIDEO_BATCH_TOOL.tagline,
      tags: VIDEO_BATCH_TOOL.features.slice(0, 4).map((f) => f.title),
      actionText: 'Xem & Tải Công Cụ',
      actionIcon: <ArrowRightOutlined />,
      actionType: 'primary',
      onClick: () => navigate(`/tools/${VIDEO_BATCH_TOOL.slug}`),
      isReady: true,
      borderColor: '#047857',
      // Chưa phát hành thì chỉ ADMIN thấy thẻ này
      adminOnly: !VIDEO_BATCH_TOOL.published,
    },
    {
      id: HEALTH_HAND_VIDEO_TOOL.slug,
      title: HEALTH_HAND_VIDEO_TOOL.name,
      subtitle: 'Phần mềm cài trên máy tính Windows',
      badge: HEALTH_HAND_VIDEO_TOOL.published ? 'ĐANG HOẠT ĐỘNG' : HEALTH_HAND_VIDEO_TOOL.stage === 'demo' ? 'DEMO' : 'BẢN NHÁP',
      badgeColor: HEALTH_HAND_VIDEO_TOOL.published ? 'success' : HEALTH_HAND_VIDEO_TOOL.stage === 'demo' ? 'processing' : 'warning',
      image: HEALTH_HAND_VIDEO_TOOL.image || '/images/tools/tool-tao-video-hang-loat.jpg',
      icon: <VideoCameraOutlined style={{ fontSize: 24, color: '#005537' }} />,
      iconBg: '#ecfdf5',
      desc: HEALTH_HAND_VIDEO_TOOL.tagline,
      tags: HEALTH_HAND_VIDEO_TOOL.features.slice(0, 4).map((f) => f.title),
      actionText: 'Xem & Tải Công Cụ',
      actionIcon: <ArrowRightOutlined />,
      actionType: 'primary',
      onClick: () => navigate(`/tools/${HEALTH_HAND_VIDEO_TOOL.slug}`),
      isReady: true,
      borderColor: '#047857',
      // Chưa phát hành thì chỉ ADMIN thấy thẻ này
      adminOnly: !HEALTH_HAND_VIDEO_TOOL.published,
    },
    {
      id: QA_VIDEO_SKILL_TOOL.slug,
      title: QA_VIDEO_SKILL_TOOL.name,
      subtitle: 'Skill ChatGPT Plus + Google Flow — có video mẫu xem trước',
      badge: QA_VIDEO_SKILL_TOOL.published ? 'MỚI' : 'BẢN NHÁP',
      badgeColor: QA_VIDEO_SKILL_TOOL.published ? 'success' : 'warning',
      image: QA_VIDEO_SKILL_TOOL.image || '/images/tools/tool-tao-video-hang-loat.jpg',
      icon: <VideoCameraOutlined style={{ fontSize: 24, color: '#005537' }} />,
      iconBg: '#ecfdf5',
      desc: QA_VIDEO_SKILL_TOOL.tagline,
      tags: QA_VIDEO_SKILL_TOOL.features.slice(0, 4).map((f) => f.title),
      actionText: 'Xem Video Mẫu & Mua',
      actionIcon: <ArrowRightOutlined />,
      actionType: 'primary',
      onClick: () => navigate(`/tools/${QA_VIDEO_SKILL_TOOL.slug}`),
      isReady: true,
      borderColor: '#047857',
      adminOnly: !QA_VIDEO_SKILL_TOOL.published,
    },
    {
      id: STORY_VIDEO_SKILL_TOOL.slug,
      title: STORY_VIDEO_SKILL_TOOL.name,
      subtitle: 'Skill ChatGPT Plus + Google Flow — có video mẫu xem trước',
      badge: STORY_VIDEO_SKILL_TOOL.published ? 'MỚI' : 'BẢN NHÁP',
      badgeColor: STORY_VIDEO_SKILL_TOOL.published ? 'success' : 'warning',
      image: STORY_VIDEO_SKILL_TOOL.image || '/images/tools/tool-tao-video-hang-loat.jpg',
      icon: <VideoCameraOutlined style={{ fontSize: 24, color: '#005537' }} />,
      iconBg: '#ecfdf5',
      desc: STORY_VIDEO_SKILL_TOOL.tagline,
      tags: STORY_VIDEO_SKILL_TOOL.features.slice(0, 4).map((f) => f.title),
      actionText: 'Xem Video Mẫu & Mua',
      actionIcon: <ArrowRightOutlined />,
      actionType: 'primary',
      onClick: () => navigate(`/tools/${STORY_VIDEO_SKILL_TOOL.slug}`),
      isReady: true,
      borderColor: '#047857',
      adminOnly: !STORY_VIDEO_SKILL_TOOL.published,
    },
    {
      id: EXPERT_VIDEO_SKILL_TOOL.slug,
      title: EXPERT_VIDEO_SKILL_TOOL.name,
      subtitle: 'Plugin ChatGPT + Google Flow — kèm trang hướng dẫn có ảnh',
      badge: EXPERT_VIDEO_SKILL_TOOL.published ? 'MỚI' : 'BẢN NHÁP',
      badgeColor: EXPERT_VIDEO_SKILL_TOOL.published ? 'success' : 'warning',
      image: EXPERT_VIDEO_SKILL_TOOL.image,
      icon: <VideoCameraOutlined style={{ fontSize: 24, color: '#005537' }} />,
      iconBg: '#ecfdf5',
      desc: EXPERT_VIDEO_SKILL_TOOL.tagline,
      tags: EXPERT_VIDEO_SKILL_TOOL.features.slice(0, 4).map((f) => f.title),
      actionText: 'Xem Video Mẫu & Mua',
      actionIcon: <ArrowRightOutlined />,
      actionType: 'primary',
      onClick: () => navigate(`/tools/${EXPERT_VIDEO_SKILL_TOOL.slug}`),
      isReady: true,
      borderColor: '#047857',
      adminOnly: !EXPERT_VIDEO_SKILL_TOOL.published,
    },
    {
      id: COMPARE_VIDEO_TOOL.slug,
      title: COMPARE_VIDEO_TOOL.name,
      subtitle: 'Video dọc so sánh 2 bên có người dẫn — có video mẫu xem trước',
      badge: COMPARE_VIDEO_TOOL.published ? 'MỚI' : 'BẢN NHÁP',
      badgeColor: COMPARE_VIDEO_TOOL.published ? 'success' : 'warning',
      image: COMPARE_VIDEO_TOOL.image,
      icon: <VideoCameraOutlined style={{ fontSize: 24, color: '#005537' }} />,
      iconBg: '#ecfdf5',
      desc: COMPARE_VIDEO_TOOL.tagline,
      tags: COMPARE_VIDEO_TOOL.features.slice(0, 4).map((f) => f.title),
      actionText: 'Xem Video Mẫu & Mua',
      actionIcon: <ArrowRightOutlined />,
      actionType: 'primary',
      onClick: () => navigate(`/tools/${COMPARE_VIDEO_TOOL.slug}`),
      isReady: true,
      borderColor: '#047857',
      adminOnly: !COMPARE_VIDEO_TOOL.published,
    },
  ].filter((tool) => !tool.adminOnly || isAdmin);

  return (
    <div className="pharmacy-tool-wrapper">
      {/* Hero Header Banner */}
      <div className="tool-hero-banner">
        <div className="main-container">
          <Row gutter={[24, 24]} align="middle" justify="space-between">
            <Col xs={24} md={16}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Tag color="#10b981" style={{ fontWeight: 700, borderRadius: 4, padding: '2px 8px' }}>
                  HỆ SINH THÁI CÔNG CỤ SỐ
                </Tag>
                <span style={{ fontSize: 13, color: '#a7f3d0' }}>Dành cho Quầy thuốc & Nhà thuốc độc lập</span>
              </div>
              <h1 className="tool-hero-title" style={{ fontSize: 32 }}>
                Trung Tâm Công Cụ Quản Trị & Vận Hành Nhà Thuốc
              </h1>
              <p className="tool-hero-subtitle" style={{ fontSize: 15.5 }}>
                Bộ công cụ thực chiến được xây dựng độc quyền nhằm hỗ trợ các dược sĩ và chủ nhà thuốc chuẩn hoá quy trình, kiểm soát dòng tiền, phát triển đội ngũ và nâng cao năng lực cạnh tranh bền vững.
              </p>
            </Col>

            <Col xs={24} md={8}>
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 14,
                  padding: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                }}
              >
                <div style={{ fontSize: 13, color: '#cbd5e1', marginBottom: 8 }}>
                  Hệ sinh thái công cụ số (4 Giải pháp):
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircleOutlined style={{ color: '#34d399' }} /> Tool 26 Quy trình SOP
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircleOutlined style={{ color: '#34d399' }} /> Tool Đo lường sức khỏe
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircleOutlined style={{ color: '#34d399' }} /> Tool Trắc nghiệm thực chiến
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircleOutlined style={{ color: '#34d399' }} /> Tool Cơ chế lương thưởng (Mới)
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Tools Cards Grid */}
      <div className="main-container" style={{ marginTop: 32 }}>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>
            Danh Sách Công Cụ Quản Trị & Vận Hành
          </h2>
          <p style={{ fontSize: 14, color: '#64748b', marginTop: 4 }}>
            Chọn công cụ bạn muốn sử dụng để bắt đầu tối ưu hoạt động tại quầy
          </p>
        </div>

        <Row gutter={[24, 24]}>
          {toolsList.map((tool) => (
            <Col xs={24} md={12} key={tool.id}>
              <Card
                className="tool-card hover-card"
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 14,
                  border: `1px solid #e2e8f0`,
                  borderTop: `4px solid ${tool.borderColor}`,
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                }}
                cover={
                  <div style={{ position: 'relative', height: 175, overflow: 'hidden', background: '#003822' }}>
                    <img
                      src={tool.image}
                      alt={tool.title}
                      className="tool-card-img"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        transition: 'transform 0.4s ease',
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(to top, rgba(0, 40, 25, 0.75) 0%, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.25) 100%)',
                      }}
                    />
                    {/* Floating Badge top-right */}
                    <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 2, display: 'flex', gap: 6 }}>
                      <Tag
                        color={tool.badgeColor}
                        style={{
                          margin: 0,
                          fontWeight: 700,
                          fontSize: 11.5,
                          borderRadius: 6,
                          padding: '3px 10px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
                          border: 'none',
                        }}
                      >
                        {tool.badge}
                      </Tag>
                    </div>
                    {/* Floating Icon badge bottom-left */}
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 12,
                        left: 16,
                        zIndex: 2,
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                        border: `2px solid ${tool.borderColor}`,
                      }}
                    >
                      {tool.icon}
                    </div>
                  </div>
                }
                styles={{
                  body: {
                    padding: '20px 24px 24px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    justifyContent: 'space-between',
                  },
                }}
                bodyStyle={{
                  padding: '20px 24px 24px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  {/* Title & Subtitle */}
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 4, marginTop: 4 }}>
                    {tool.title}
                  </h3>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0284c7', marginBottom: 12 }}>
                    {tool.subtitle}
                  </div>
                  {isFree(tool.id) && (
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: '#15803d' }}>Miễn phí</div>
                  )}

                  {/* Description */}
                  <p style={{ fontSize: 13.5, color: '#475569', lineHeight: 1.6, marginBottom: 16 }}>
                    {tool.desc}
                  </p>

                  {/* Highlight Tags */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
                    {tool.tags.map((tag, idx) => (
                      <Tag
                        key={idx}
                        style={{
                          margin: 0,
                          fontSize: 11.5,
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: 6,
                          color: '#334155',
                          padding: '2px 8px',
                        }}
                      >
                        ✓ {tag}
                      </Tag>
                    ))}
                  </div>
                </div>

                {/* Action Button */}
                <div style={{ paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
                  <Button
                    type={tool.actionType}
                    size="large"
                    block
                    icon={tool.actionIcon}
                    onClick={tool.onClick}
                    className={tool.isReady ? 'tool-cta-btn' : undefined}
                    style={{
                      borderRadius: 8,
                      fontWeight: 700,
                      height: 44,
                      ...(tool.isReady
                        ? {
                          background: '#005537',
                          borderColor: '#005537',
                          boxShadow: '0 4px 12px rgba(0, 85, 55, 0.2)',
                        }
                        : {
                          color: '#475569',
                          borderColor: '#cbd5e1',
                        }),
                    }}
                  >
                    {tool.isReady ? (
                      <span className="tool-cta-text" aria-label={tool.actionText}>
                        {Array.from(tool.actionText).map((ch, i) => (
                          <span key={i} aria-hidden="true" className="tool-cta-char" style={{ '--i': i }}>
                            {ch}
                          </span>
                        ))}
                      </span>
                    ) : (
                      tool.actionText
                    )}
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

    </div>
  );
}
